import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Auth.css';

/* ─── Shared field component ─────────────────────────────────────────────── */
const Field = ({ label, id, type = 'text', value, onChange, placeholder, autoComplete, required }) => (
  <div className="auth-field">
    <label className="auth-field__label" htmlFor={id}>{label}</label>
    <input
      id={id}
      type={type}
      className="auth-field__input"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete={autoComplete}
      required={required}
    />
  </div>
);

/* ─── Login ──────────────────────────────────────────────────────────────── */
export const Login = () => {
  const [form, setForm]     = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const navigate            = useNavigate();
  const location            = useLocation();
  const { login }           = useAuth();

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login({ email: form.email, password: form.password });
      // Send the user back to whatever protected page they were trying to
      // reach before ProtectedRoute redirected them here (defaults to /profile).
      const redirectTo = location.state?.from?.pathname || '/chat';
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-page__glow" aria-hidden="true" />

      <div className="auth-card glass-card">
        {/* Header */}
        <Link to="/" className="auth-card__logo">
          <span className="auth-logo-mark">M</span>
          <span className="auth-logo-text">Margin<span className="gold-text">Pro</span></span>
        </Link>

        <div className="auth-card__header">
          <h1 className="auth-card__title">Welcome back.</h1>
          <p className="auth-card__sub">Log in to your account to continue.</p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <Field
            label="Email address"
            id="login-email"
            type="email"
            value={form.email}
            onChange={set('email')}
            placeholder="you@company.com"
            autoComplete="email"
            required
          />
          <Field
            label="Password"
            id="login-password"
            type="password"
            value={form.password}
            onChange={set('password')}
            placeholder="••••••••"
            autoComplete="current-password"
            required
          />

          {error && <p className="auth-form__error">{error}</p>}

          <div className="auth-form__row">
            <a href="#forgot" className="auth-link auth-link--sm">Forgot password?</a>
          </div>

          <button
            type="submit"
            className={`btn btn--gold btn--lg auth-submit${loading ? ' auth-submit--loading' : ''}`}
            disabled={loading}
          >
            {loading ? <span className="auth-spinner" /> : 'Log In'}
          </button>
        </form>

        <p className="auth-card__switch">
          New here?{' '}
          <Link to="/signup" className="auth-link">Create an account</Link>
        </p>
      </div>
    </div>
  );
};

/* ─── Signup ─────────────────────────────────────────────────────────────── */
export const Signup = () => {
  const [form, setForm]     = useState({
    firstName: '', lastName: '', email: '', password: '', confirm: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const navigate            = useNavigate();
  const location            = useLocation();
  const { signup }          = useAuth();

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      await signup({
        firstName: form.firstName,
        lastName:  form.lastName,
        email:     form.email,
        password:  form.password,
      });
      const redirectTo = location.state?.from?.pathname || '/chat';
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-page__glow" aria-hidden="true" />

      <div className="auth-card auth-card--wide glass-card">
        {/* Header */}
        <Link to="/" className="auth-card__logo">
          <span className="auth-logo-mark">M</span>
          <span className="auth-logo-text">Margin<span className="gold-text">Pro</span></span>
        </Link>

        <div className="auth-card__header">
          <h1 className="auth-card__title">Create your account.</h1>
          <p className="auth-card__sub">Free to start. No credit card required.</p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="auth-form__grid">
            <Field
              label="First name"
              id="signup-first"
              value={form.firstName}
              onChange={set('firstName')}
              placeholder="Alex"
              autoComplete="given-name"
              required
            />
            <Field
              label="Last name"
              id="signup-last"
              value={form.lastName}
              onChange={set('lastName')}
              placeholder="Morgan"
              autoComplete="family-name"
              required
            />
          </div>

          <Field
            label="Email address"
            id="signup-email"
            type="email"
            value={form.email}
            onChange={set('email')}
            placeholder="you@company.com"
            autoComplete="email"
            required
          />
          <Field
            label="Password"
            id="signup-password"
            type="password"
            value={form.password}
            onChange={set('password')}
            placeholder="At least 8 characters"
            autoComplete="new-password"
            required
          />
          <Field
            label="Confirm password"
            id="signup-confirm"
            type="password"
            value={form.confirm}
            onChange={set('confirm')}
            placeholder="••••••••"
            autoComplete="new-password"
            required
          />

          {error && <p className="auth-form__error">{error}</p>}

          <p className="auth-form__disclaimer">
            By creating an account you agree to our{' '}
            <Link to="/" className="auth-link">Terms of Service</Link> and{' '}
            <Link to="/" className="auth-link">Privacy Policy</Link>.
          </p>

          <button
            type="submit"
            className={`btn btn--gold btn--lg auth-submit${loading ? ' auth-submit--loading' : ''}`}
            disabled={loading}
          >
            {loading ? <span className="auth-spinner" /> : 'Create Account'}
          </button>
        </form>

        <p className="auth-card__switch">
          Already have an account?{' '}
          <Link to="/login" className="auth-link">Log in</Link>
        </p>
      </div>
    </div>
  );
};
